import { useNavigate, useSearchParams } from "react-router-dom";
import { EmailAuthForm } from "../components/EmailAuthForm";
import { AppleSignInButton } from "../components/AppleSignInButton";
import { AppleSignInWebButton } from "../components/AppleSignInWebButton";
import { PromoScarcityBanner } from "../components/PromoScarcityBanner";
import { PromoCodeField } from "../components/PromoCodeField";
import { OpenInAppCTA } from "../components/OpenInAppCTA";
import { showGoogleSignIn } from "../lib/platform";
import { useAuth } from "../App";
import useSeo from "../hooks/useSeo";
import { PublicHeader } from "../components/PublicHeader";
import { SiteFooter } from "../components/SiteFooter";

// Anonymous → "Start 5 Free Days" or "Unlock everything now" → here.
// Two intents:
//   • intent=trial → start 5-day trial; after auth land on /dashboard
//   • intent=pay   → after auth land on /pricing so they can pay & unlock
const SignupPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const intent = params.get("intent") || "trial";
  const plan = params.get("plan") || "annual";

  const isPayNow = intent === "pay";
  useSeo({
    title: isPayNow
      ? "Sign up — Unlock everything | Bilateral Calm"
      : "Start your 5-day free trial — Bilateral Calm",
    description: isPayNow
      ? "Create your Bilateral Calm account to unlock the full library of bilateral stimulation and bilateral sound practices for anxiety, insomnia, stress, and trauma — inspired by EMDR (Eye Movement Desensitization and Reprocessing)."
      : "Start a free 5-day trial of Bilateral Calm — bilateral sound, breath cues, and structured practices for anxiety, insomnia, stress, and trauma. Inspired by EMDR principles.",
    path: isPayNow ? `/signup?intent=pay&plan=${plan}` : "/signup",
  });
  // Trial accounts always land on the dashboard. Pay-now accounts get sent
  // back to /pricing where the same "Unlock everything now" button now
  // succeeds (they're authenticated → Stripe Checkout fires immediately).
  const redirectTo = isPayNow ? `/pricing?plan=${plan}&autoPay=1` : "/dashboard";

  const startGoogle = () => {
    // Stash where to go after Google OAuth completes — AuthCallback reads
    // this back and routes there instead of the default /dashboard.
    try { sessionStorage.setItem("post_auth_redirect", redirectTo); } catch (_) { /* ignore */ }
    login();
  };

  return (
    <div
      className="min-h-screen bg-[#FBF8F1] flex flex-col"
      data-testid="signup-page"
    >
      <PublicHeader />
      <div className="flex-1 flex items-start sm:items-center justify-center px-6 py-8 sm:py-12">
      <div className="max-w-md w-full text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#3090A8] font-medium mb-3">
          {isPayNow ? "Unlock everything" : "Start your free trial"}
        </p>
        <h1 className="font-outfit font-semibold text-3xl sm:text-4xl text-[#1B2349] tracking-tight mb-3">
          {isPayNow ? "Almost there." : "5 free days, then $5.83 / mo."}
        </h1>
        <p className="text-sm sm:text-base text-[#4A5568] leading-relaxed mb-3">
          {isPayNow
            ? "Create your account and we'll take you straight to checkout."
            : "Create your account to start the trial. We'll send a gentle reminder before your card is charged on day 6."}
        </p>
        <p className="text-xs text-[#718096] mb-6">
          Plan selected: <strong className="text-[#1B2349]">{plan === "annual" ? "Annual ($69.99 / yr)" : "Monthly ($12.99 / mo)"}</strong> · Cancel anytime.
        </p>

        {/* Scarcity / waitlist banner — hidden unless remaining ≤ 15 or full. */}
        <div className="mb-4">
          <PromoScarcityBanner variant="compact" />
        </div>

        {/* Promo code field (collapsed by default). Persists across the
            Apple/Google OAuth round-trip via sessionStorage. */}
        <div className="mb-4 flex justify-start">
          <PromoCodeField label="Have an early-access code?" />
        </div>

        {/* iPhone / iPad Safari steer — hidden on desktop, Android, and the
            App Store app. Shown here because those users no longer see the
            Google button and might otherwise wonder where it went. */}
        <OpenInAppCTA />

        {/* Apple Sign-In first — on the iOS App Store build this is the
            fastest one-tap flow. Returns null on non-iOS, so web users
            see Google + email below instead. */}
        <div className="mb-3">
          <AppleSignInButton redirectTo={redirectTo} />
        </div>

        {/* Web Apple Sign-In — renders only on the web when
            REACT_APP_APPLE_WEB_SERVICES_ID is configured. Same backend
            endpoint as native. */}
        <div className="mb-3">
          <AppleSignInWebButton
            label="Continue with Apple"
            onSuccess={() => (window.location.href = redirectTo || "/dashboard")}
          />
        </div>

        {/* Google — visible on desktop web + Android web. Hidden on iOS
            Capacitor and iPhone/iPad Safari (see platform.js). */}
        {showGoogleSignIn() && (
          <button
            onClick={startGoogle}
            className="w-full justify-center rounded-full bg-[#1B2349] text-white font-medium py-3 hover:bg-[#0F1838] active:scale-[0.98] transition-all duration-200 mb-3 inline-flex items-center gap-2"
            data-testid="signup-google-btn"
          >
            Continue with Google
          </button>
        )}

        <div className="text-xs uppercase tracking-widest text-[#A0AEC0] my-3">or</div>

        <EmailAuthForm mode="signup" redirectTo={redirectTo} />

        <p className="mt-6 text-xs text-[#718096]">
          {isPayNow
            ? "Prefer to try free first?"
            : "Want everything unlocked from day one?"}{" "}
          <button
            onClick={() =>
              navigate(`/signup?plan=${plan}&intent=${isPayNow ? "trial" : "pay"}`)
            }
            className="underline underline-offset-2 text-[#3090A8] hover:text-[#1B2349]"
            data-testid="signup-flip-intent-btn"
          >
            {isPayNow ? "Start 5-day free trial" : "Unlock everything now"}
          </button>
        </p>
      </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default SignupPage;
